import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import React from 'react';
import { AvatarDropdown, AvatarName, Footer, Question } from '@/components';
import NoticeWebSocket from '@/components/NoticeWebSocket';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

// 菜单数据结构（type: M 目录 / C 菜单 / F 按钮权限，F 不展示在侧栏）
interface MenuItem {
  path: string;
  name: string;
  type?: string;
  iconPath?: string;
  showChildren?: boolean;
  children?: MenuItem[];
}

const isDev = process.env.NODE_ENV === 'development' || process.env.CI;
const loginPath = '/user/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };

  // 从 localStorage 读取用户信息
  const getUserFromStorage = (): API.CurrentUser | undefined => {
    try {
      const userDetailStr = localStorage.getItem('userDetail');
      if (userDetailStr) {
        const userDetail = JSON.parse(userDetailStr);
        const user = userDetail?.user;
        if (user) {
          return {
            name: user.userName || user.loginName || userDetail.username,
            avatar: '',
            userid: user.userId || userDetail.id,
          } as API.CurrentUser;
        }
      }
    } catch (error) {
      console.error('读取用户信息失败:', error);
    }
    return undefined;
  };

  // 如果不是登录页面，执行
  const { location } = history;
  if (![loginPath, '/user/register', '/user/register-result'].includes(location.pathname)) {
    let currentUser: API.CurrentUser | undefined = getUserFromStorage();
    // 如果 localStorage 没有用户信息，尝试从服务器获取
    if (!currentUser) {
      currentUser = await fetchUserInfo();
    }
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// 将后台菜单数据转换为 ProLayout 需要的菜单格式（过滤 type=F 的按钮权限）
function convertBackendMenuToProMenu(backendMenus: MenuItem[]): any[] {
  return backendMenus
    .filter((menu) => menu.type !== 'F' && menu.path !== '#')
    .map((menu) => {
      const proMenu: any = {
        path: menu.path,
        name: menu.name,
        icon: menu.iconPath && menu.iconPath !== '#' ? menu.iconPath : undefined,
      };

      if (menu.children?.length) {
        const childRoutes = convertBackendMenuToProMenu(menu.children);
        if (childRoutes.length > 0) {
          proMenu.routes = childRoutes;
        }
      }

      return proMenu;
    });
}

export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  // 从 localStorage 读取用户名的辅助函数
  const getUserNameFromStorage = (): string | undefined => {
    try {
      const userDetailStr = localStorage.getItem('userDetail');
      if (userDetailStr) {
        const userDetail = JSON.parse(userDetailStr);
        const user = userDetail?.user;
        return user?.userName || user?.loginName || userDetail?.username;
      }
    } catch (error) {
      console.error('读取用户名失败:', error);
    }
    return undefined;
  };

  // 从 localStorage 获取用户菜单
  const getMenuData = () => {
    try {
      const menuDataStr = localStorage.getItem('newMenus');
      if (menuDataStr) {
        const backendMenus: MenuItem[] = JSON.parse(menuDataStr);
        console.log('📋 加载用户菜单:', backendMenus);
        return convertBackendMenuToProMenu(backendMenus);
      }
    } catch (error) {
      console.error('❌ 读取菜单数据失败:', error);
    }
    return undefined;
  };

  return {
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name || getUserNameFromStorage() || '',
    // },
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      const hasUser = initialState?.currentUser || localStorage.getItem('userDetail');
      if (!hasUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    // 自定义菜单数据
    menu: {
      locale: false,
      request: async () => {
        const menuData = getMenuData();
        if (menuData && menuData.length > 0) {
          console.log('✅ 使用动态菜单');
          return menuData;
        }
        // 如果没有动态菜单，返回空数组（隐藏所有菜单）
        console.log('⚠️ 没有菜单数据，隐藏所有菜单');
        return [];
      },
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      const hasUser = initialState?.currentUser || localStorage.getItem('userDetail');
      return (
        <>
          {hasUser && <NoticeWebSocket />}
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: 'https://proapi.azurewebsites.net',
  ...errorConfig,
};
